import React, { useState, useMemo, useEffect } from "react";
import { PropTypes } from "prop-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import { ExpandMore, UnfoldMore, UnfoldLess } from "@mui/icons-material";
import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CAlert,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { freeSet } from "@coreui/icons";
import LoadingOverlay from "react-loading-overlay-ts";
import { Loading } from "src/reusable/LoadingSpinner";
import PulseLoader from "react-spinners/PulseLoader";
import { useQueryClient } from "react-query";
import { useGetJobLogs } from "./services/JobsServices";

const STEP_PATTERN = /Running step (.+?), type (.+?)(?:,|$)/;

function parseLogIntoSections(rawText) {
  if (!rawText) return [];
  const lines = rawText.split("\n");
  const sections = [];
  let current = { name: "Setup", lines: [] };
  let stepCounter = 0;

  for (const line of lines) {
    const match = line.match(STEP_PATTERN);
    if (match) {
      if (current.lines.length > 0) sections.push(current);
      stepCounter++;
      current = {
        name: match[1],
        type: match[2],
        stepNum: stepCounter,
        lines: [line],
      };
    } else if (line.includes("Deleting sqllite database") && stepCounter > 0) {
      if (current.lines.length > 0) sections.push(current);
      current = { name: "Teardown", lines: [line] };
    } else {
      current.lines.push(line);
    }
  }
  if (current.lines.length > 0) sections.push(current);
  return sections;
}

const LogStepSection = ({ section, expanded, onToggle, index }) => {
  const theme = useTheme();
  const isSetup = section.name === "Setup";
  const isTeardown = section.name === "Teardown";
  const badgeColor = isSetup ? "#0288d1" : isTeardown ? "#ed6c02" : "#1976d2";
  const badgeLabel = isSetup
    ? "Setup"
    : isTeardown
      ? "Teardown"
      : `Step ${section.stepNum}`;

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExp) => onToggle(isExp)}
      data-testid={`job-logs-section-${index}`}
      data-attr={`jobs:card:log-section-${index}`}
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: "transparent",
        "&:before": { display: "none" },
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          minHeight: 44,
          px: 2,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            my: 0,
          },
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          },
        }}
      >
        <span
          data-testid={`job-logs-section-badge-${index}`}
          style={{
            backgroundColor: badgeColor,
            color: "#fff",
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "2px 10px",
            borderRadius: "12px",
            marginRight: "8px",
            lineHeight: "20px",
          }}
        >
          {badgeLabel}
        </span>
        <span
          style={{ fontWeight: 600, fontSize: "0.875rem", marginRight: "6px" }}
        >
          {section.name}
        </span>
        {section.type && (
          <span
            style={{ color: "#666", fontSize: "0.875rem", marginRight: "6px" }}
          >
            ({section.type})
          </span>
        )}
        <span style={{ color: "#666", fontSize: "0.875rem" }}>
          &mdash; {section.lines.length} lines
        </span>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <pre
          data-testid={`job-logs-section-content-${index}`}
          style={{
            fontSize: "0.8rem",
            fontFamily: "monospace",
            backgroundColor: "#1e1e1e",
            color: "#d4d4d4",
            padding: "12px",
            margin: 0,
            overflow: "auto",
            maxHeight: "400px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {section.lines.join("\n")}
        </pre>
      </AccordionDetails>
    </Accordion>
  );
};

LogStepSection.propTypes = {
  section: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    stepNum: PropTypes.number,
    lines: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export function JobRunLogsModal({ isVisible, onClose, jobRunId }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState(null);
  const queryClient = useQueryClient();

  const {
    data: rawLogs,
    isFetching,
    error,
  } = useGetJobLogs(jobRunId, isVisible);

  const sections = useMemo(() => parseLogIntoSections(rawLogs), [rawLogs]);

  // Initialize all expanded when sections first load
  useEffect(() => {
    if (sections.length > 0 && expandedSections === null) {
      const init = {};
      sections.forEach((_, i) => {
        init[i] = true;
      });
      setExpandedSections(init);
    }
  }, [sections, expandedSections]);

  useEffect(() => {
    setExpandedSections(null);
  }, [jobRunId]);

  const expanded = expandedSections || {};

  const handleToggleAll = () => {
    const allExp = sections.every((_, i) => expanded[i]);
    const next = {};
    sections.forEach((_, i) => {
      next[i] = !allExp;
    });
    setExpandedSections(next);
  };

  const handleRefresh = () => {
    setExpandedSections(null);
    queryClient.invalidateQueries(["JobLogs", jobRunId]);
  };

  const handleCopy = async () => {
    if (!rawLogs) return;
    try {
      await navigator.clipboard.writeText(rawLogs);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = rawLogs;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!rawLogs) return;
    setIsDownloading(true);
    try {
      const blob = new Blob([rawLogs], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `etl_job_${jobRunId}.log`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  };

  const allExpanded =
    sections.length > 0 && sections.every((_, i) => expanded[i]);

  return (
    <CModal
      show={isVisible}
      onClose={onClose}
      style={{ textAlign: "left" }}
      size="xl"
      data-testid="job-logs-modal"
      data-attr="jobs:modal:view-run-logs"
    >
      <CModalHeader
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h5 style={{ margin: 0 }}>Job Run {jobRunId} Logs</h5>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <IconButton
            size="small"
            onClick={handleToggleAll}
            data-testid="job-logs-toggle-all-btn"
            data-attr="jobs:btn:toggle-all-log-sections"
            title={allExpanded ? "Collapse All" : "Expand All"}
            aria-label={
              allExpanded ? "Collapse All Sections" : "Expand All Sections"
            }
          >
            {allExpanded ? <UnfoldLess /> : <UnfoldMore />}
          </IconButton>
          <CButton
            color="info"
            size="sm"
            data-testid="job-logs-refresh-btn"
            data-attr="jobs:btn:refresh-logs"
            onClick={handleRefresh}
          >
            <CIcon content={freeSet.cilReload} />
          </CButton>
          <CButton
            color="secondary"
            size="sm"
            data-testid="job-logs-close-btn"
            data-attr="jobs:btn:close-logs-modal"
            onClick={onClose}
          >
            <CIcon name="cil-x" />
          </CButton>
        </div>
      </CModalHeader>
      <LoadingOverlay
        active={isFetching}
        spinner={<Loading height={"75em"} width={"75em"} />}
        text="Loading Logs..."
      >
        <CModalBody
          style={{ maxHeight: "70vh", overflow: "auto" }}
          data-testid="job-logs-body"
        >
          {error ? (
            <CAlert color="danger" data-testid="job-logs-error-alert">
              {error?.response?.data?.detail || JSON.stringify(error)}
            </CAlert>
          ) : sections.length > 0 ? (
            sections.map((section, idx) => (
              <LogStepSection
                key={idx}
                section={section}
                index={idx}
                expanded={!!expanded[idx]}
                onToggle={(isExp) =>
                  setExpandedSections((prev) => ({
                    ...prev,
                    [idx]: isExp,
                  }))
                }
              />
            ))
          ) : !isFetching ? (
            <span style={{ color: "#999" }} data-testid="job-logs-no-data">
              No logs available.
            </span>
          ) : null}
        </CModalBody>
        <CModalFooter>
          <CButton
            color="info"
            disabled={!!error || !rawLogs}
            onClick={handleCopy}
            data-testid="job-logs-copy-btn"
            data-attr="jobs:btn:copy-logs-to-clipboard"
          >
            {copied ? "Copied!" : "Copy to Clipboard"}
          </CButton>
          <CButton
            color="secondary"
            disabled={!!error || !rawLogs}
            onClick={handleDownload}
            data-testid="job-logs-download-btn"
            data-attr="jobs:btn:download-logs"
          >
            {isDownloading ? (
              <PulseLoader size={15} color={"#9EA59D"} loading />
            ) : (
              "Download Logs"
            )}
          </CButton>
        </CModalFooter>
      </LoadingOverlay>
    </CModal>
  );
}

JobRunLogsModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  jobRunId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
